import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../constants';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { CalendarNavigationConfig } from '../types';

interface Props {
  onScroll: (direction: 'left' | 'right') => void;
  navigationConfig?: CalendarNavigationConfig;
}
export const FloatingScrollButtons = ({ onScroll, navigationConfig }: Props) => {
  const { canScrollLeft, canScrollRight } = useCalendarGridStore(['canScrollLeft', 'canScrollRight']);

  // Date navigation replaces horizontal scrolling, so the floating buttons are redundant there.
  if (navigationConfig || (!canScrollLeft && !canScrollRight)) return null;

  return (
    <div css={overlayStyle}>
      {canScrollLeft && (
        <Button
          css={[buttonStyle, leftButtonStyle]}
          variant='secondary'
          iconName='caret-left'
          label='Scroll left'
          onClick={() => onScroll('left')}
        />
      )}
      {canScrollRight && (
        <Button
          css={[buttonStyle, rightButtonStyle]}
          variant='secondary'
          iconName='caret-right'
          label='Scroll right'
          onClick={() => onScroll('right')}
        />
      )}
    </div>
  );
};

const overlayStyle = css({
  gridColumn: '1 / -1',
  gridRow: '1 / -1',
  position: 'sticky',
  top: 0,
  left: 0,
  alignSelf: 'start',
  justifySelf: 'start',
  height: 'var(--viewport-height)',
  width: 'var(--viewport-width)',
  boxSizing: 'border-box',
  zIndex: CALENDAR_Z_INDICES.floatingScrollButtons,
  pointerEvents: 'none', // let clicks/drag-create pass through the empty middle
});

const buttonStyle = css({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'auto',
  width: 42,
  minWidth: 42,
  height: 42,
  borderRadius: '50%',
  background: theme.colors.neutral10,
  border: `1px solid ${theme.colors.neutral20}`,
  transition: 'box-shadow 0.2s ease-in-out',
  ':hover': {
    boxShadow: theme.shadows.light,
  },
});

const leftButtonStyle = css({ left: theme.spacing(1) });
const rightButtonStyle = css({ right: theme.spacing(1) });
